<template>
  <DialogRoot :open="open" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay class="app-dialog-overlay" />
      <DialogContent
        :class="['app-dialog-content', contentClass]"
        :aria-describedby="undefined"
      >
        <slot :close="closeDialog" />
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { DialogContent, DialogOverlay, DialogPortal, DialogRoot } from 'radix-vue'

const props = defineProps({
  modalId: {
    type: String,
    required: true
  },
  open: {
    type: Boolean,
    default: undefined
  },
  contentClass: {
    type: [String, Array, Object],
    default: ''
  }
})

const emit = defineEmits(['update:open'])
const internalOpen = ref(false)
const isControlled = computed(() => typeof props.open === 'boolean')
const open = computed({
  get() {
    return isControlled.value ? props.open : internalOpen.value
  },
  set(value) {
    if (isControlled.value) {
      emit('update:open', value)
      return
    }

    internalOpen.value = value
  }
})

function ensureDialogRegistry() {
  if (!window.sccDialogRegistry) {
    window.sccDialogRegistry = {}
  }

  return window.sccDialogRegistry
}

function modalElement() {
  return document.getElementById(props.modalId)
}

function dispatchModalEvent(eventName) {
  const modal = modalElement()

  if (!modal) {
    return
  }

  if (eventName === 'shown.bs.modal') {
    modal.removeAttribute('aria-hidden')
    modal.setAttribute('aria-modal', 'true')
    modal.style.display = 'block'
  } else if (eventName === 'hidden.bs.modal') {
    modal.setAttribute('aria-hidden', 'true')
    modal.removeAttribute('aria-modal')
    modal.style.display = 'none'
  }

  modal.dispatchEvent(new CustomEvent(eventName, {
    bubbles: true,
    detail: { modalId: props.modalId }
  }))
}

function closeDialog() {
  open.value = false
}

function handleOpenChange(value) {
  open.value = value
}

function handleOpenRequest() {
  open.value = true
}

function handleCloseRequest() {
  open.value = false
}

watch(open, (value, previousValue) => {
  if (value === previousValue) {
    return
  }

  dispatchModalEvent(value ? 'shown.bs.modal' : 'hidden.bs.modal')
})

onMounted(() => {
  const modal = modalElement()

  if (!modal) {
    return
  }

  modal.setAttribute('aria-hidden', 'true')
  modal.style.display = 'none'
  modal.addEventListener('scc:modal:open', handleOpenRequest)
  modal.addEventListener('scc:modal:close', handleCloseRequest)
  ensureDialogRegistry()[props.modalId] = {
    open: handleOpenRequest,
    close: handleCloseRequest
  }
})

onBeforeUnmount(() => {
  const modal = modalElement()

  if (!modal) {
    return
  }

  modal.removeEventListener('scc:modal:open', handleOpenRequest)
  modal.removeEventListener('scc:modal:close', handleCloseRequest)
  delete ensureDialogRegistry()[props.modalId]
})
</script>
